import React, { useState, useEffect } from "react"
import { api } from "aldoo-ra/API"
import { url } from "aldoo-ra/CMS/url"
// import Value from "aldoo-ra/Value" // Uncomment when reverting to site language
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"

// Initialize API
const hcAPI = api("help-center")

/**
 * PopularCategories component for the Help Center
 * Self-contained component that fetches and displays popular categories
 * Now filters out categories with draft status
 */
const PopularCategories = () => {
  const texts = TextBundle({ id: "help-center" })
  const [categories, setCategories] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sectionName, setSectionName] = useState("Most Popular")

  // Force English for help center
  const [currentLanguage] = useState("en")
  
  // To revert to site language, uncomment below and comment out the line above:
  // const [currentLanguage] = Value({
  //   key: "current-site-language",
  //   defaultValue: "en",
  //   persistKey: "current-site-language",
  // })

  // Helper function to check if a category is published
  const isPublished = (category) => {
    return category.status === "published"
  }

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true)
        const response = await hcAPI({
          get_content: {
            contentURL: "/category/most-popular",
            language: currentLanguage,
          },
        })

        if (response && response.content && response.content.categories) {
          // Filter categories to only include published ones
          const publishedCategories =
            response.content.categories.filter(isPublished)
          setCategories(publishedCategories)
          setSectionName(response.content.name || "Most Popular") // Set the section name if available
        } else {
          setCategories([])
        }

        setIsLoading(false)
      } catch (err) {
        console.error("Error fetching categories:", err)
        setError("Failed to load categories")
        setIsLoading(false)
      }
    }

    fetchCategories()
  }, [currentLanguage])

  // Handle navigation when a category is clicked
  const handleCategoryClick = (slug) => {
    // Navigate to the category page with the correct path
    window.location.href = `/help-center/category/${slug}`
  }

  if (isLoading) {
    return (
      <div className="py-8 text-center">
        {/* Spinner is hidden (the whole page has a custom loader) */}
        {/* <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div> */}
        <Typography variant="txt1_dim">
          {texts?.loading_popular_cat || "Loading popular categories..."}
        </Typography>
      </div>
    )
  }

  if (error) {
    return <Typography variant="error">{error}</Typography>
  }

  if (!categories || categories.length === 0) {
    return null
  }

  return (
    <div>
      <Typography
        variant="h2"
        className="text-center md:text-left mb-6 md:mb-11"
      >
        {sectionName || "Most Popular"}
      </Typography>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
        {categories.map((category) => (
          <div
            key={category._id}
            className="h-[186px] flex flex-col items-center justify-center bg-paper border border-bordered hover:border-secondary rounded-[30px] p-8 cursor-pointer hover:shadow-soft_lg transition-shadow"
            onClick={() => handleCategoryClick(category.slug)}
          >
            {category.icon && (
              <div className="h-[60px] w-[60px] mb-4">
                <img
                  src={url(category.icon)}
                  alt={`${category.label} icon`}
                  className="h-full w-auto object-contain"
                />
              </div>
            )}
            <Typography variant="h4" className="text-left my-0">
              {category.label}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  )
}

export default PopularCategories
